"use client"

import { useEffect, useState, useRef, useCallback } from "react"
import { DatePicker } from "@mui/x-date-pickers"
import { Toast } from "primereact/toast"
import dayjs from "dayjs"
import {
  CalendarDays,
  ChevronDown,
  Search,
  TrendingUp,
  Upload,
  Users,
  Building,
  Shield,
  FileText,
  Clock,
  Database,
  Sparkles,
  Zap,
  X,
} from "lucide-react"
import axios from "axios"
import { ClimbingBoxLoader } from "react-spinners"
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts"

// You'll need to replace this with your actual API base URL
const API_BASE_URL = "https://api.rcubedev.tickbox.co.in/"

// Animated Counter Component
const AnimatedCounter = ({ value, duration = 2000 }) => {
  const [count, setCount] = useState(0)

  useEffect(() => {
    let startTime = null
    const startValue = 0
    const endValue = value

    const animate = (currentTime) => {
      if (startTime === null) startTime = currentTime
      const progress = Math.min((currentTime - startTime) / duration, 1)

      const easeOutQuart = 1 - Math.pow(1 - progress, 4)
      const currentCount = Math.floor(startValue + (endValue - startValue) * easeOutQuart)

      setCount(currentCount)

      if (progress < 1) {
        requestAnimationFrame(animate)
      }
    }

    requestAnimationFrame(animate)
  }, [value, duration])

  return <span>{count.toLocaleString()}</span>
}

// Custom Glassmorphism Card Components
const Card = ({ children, className = "", variant = "default", gradient = "", ...props }) => {
  const baseStyle = "backdrop-blur-xl bg-gradient-to-br border border-white/20"
  const variantStyles = {
    default: `${baseStyle} ${gradient}`,
  }

  return (
    <div
      className={`${variantStyles[variant]} rounded-2xl shadow-2xl transition-all duration-300 group relative overflow-hidden ${className}`}
      {...props}
    >
      {children}
    </div>
  )
}

const CardHeader = ({ children, className = "", ...props }) => {
  return (
    <div className={`p-4 pb-2 ${className}`} {...props}>
      {children}
    </div>
  )
}

const CardContent = ({ children, className = "", ...props }) => {
  return (
    <div className={`px-4 pb-4 ${className}`} {...props}>
      {children}
    </div>
  )
}

const CardTitle = ({ children, className = "", ...props }) => {
  return (
    <h3
      className={`text-sm font-bold text-transparent bg-clip-text bg-gradient-to-r from-white to-white/80 uppercase tracking-wide ${className}`}
      {...props}
    >
      {children}
    </h3>
  )
}

const CardDescription = ({ children, className = "", ...props }) => {
  return (
    <p className={`text-xs text-white/70 mt-1 ${className}`} {...props}>
      {children}
    </p>
  )
}

// Custom Tooltip Component for Bar Charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-gray-800/95 backdrop-blur-xl border border-gray-600/50 rounded-lg p-3 text-white text-sm">
        <p className="font-bold mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={`item-${index}`} className="text-white">
            {`${entry.name}: ${entry.value}`}
          </p>
        ))}
      </div>
    )
  }
  return null
}

const Dashboard = () => {
  const toast = useRef(null)
  const [formData, setFormData] = useState({
    companyName: "",
    projectName: [],
    state: [],
    location: [],
  })

  const [allFilterData, setAllFilterData] = useState([])
  const [dropdownOptions, setDropdownOptions] = useState({
    company_names: [],
    project_names: [],
    states: [],
    locations: [],
  })

  const [originalDropdownOptions, setOriginalDropdownOptions] = useState({
    company_names: [],
    project_names: [],
    states: [],
    locations: [],
  })

  const [cascadingOptions, setCascadingOptions] = useState({
    company_names: [],
    project_names: [],
    states: [],
    locations: [],
  })

  const [openDropdown, setOpenDropdown] = useState(null)
  const [searchTerms, setSearchTerms] = useState({
    companyName: "",
    projectName: "",
    state: "",
    location: "",
  })

  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)
  const [dashboardData, setDashboardData] = useState({
    total_months: 0,
    yet_upload: 0,
    uploaded: 0,
    total_client: 0,
    licensed_site: 0,
    non_licensed_site: 0,
    total_record: 0,
    total_employee: 0,
  })
  const [isLoading, setIsLoading] = useState(false)
  const [filtersApplied, setFiltersApplied] = useState(false)

  const client_id = typeof window !== "undefined" ? sessionStorage.getItem("client_id") : null
  const role_id = typeof window !== "undefined" ? sessionStorage.getItem("user_role_id") : null
  const token = typeof window !== "undefined" ? sessionStorage.getItem("token") : null
  const username = typeof window !== "undefined" ? sessionStorage.getItem("current_username") : null
  const company_name = typeof window !== "undefined" ? sessionStorage.getItem("company_name") : null

  const isRoleOne = role_id === "1" || role_id === 1

  const searchInputRef = useRef(null)
  const dropdownScrollRef = useRef(null)

  const showSuccess = (summary, detail, options) => {
    toast.current?.show({
      severity: "success",
      summary: summary,
      detail: detail,
      life: 3000,
      closable: true,
      ...options,
    })
  }

  const showError = (summary, detail, options) => {
    toast.current?.show({
      severity: "error",
      summary: summary,
      detail: detail,
      life: 3000,
      closable: true,
      ...options,
    })
  }

  const showWarn = (summary, detail) => {
    toast.current?.show({
      severity: "warn",
      summary: summary,
      detail: detail,
      life: 3000,
      closable: true,
    })
  }

  const handleApiError = (error, defaultMessage = "SOMETHING WENT WRONG!!!") => {
    if (error.response) {
      const status = error.response.status
      if (status === 500) {
        return "SOMETHING WENT WRONG!!!"
      }
      const errorMessage =
        error.response.data?.message ||
        error.response.data?.error ||
        error.response.data?.detail ||
        error.response.statusText ||
        defaultMessage
      return errorMessage
    } else if (error.request) {
      return "Network error. Please check your connection and try again."
    } else {
      return error.message || defaultMessage
    }
  }

  const handleSearchTermChange = (field, value) => {
    setSearchTerms((prev) => ({
      ...prev,
      [field]: value,
    }))

    setTimeout(() => {
      if (searchInputRef.current && openDropdown === field) {
        searchInputRef.current.focus()
      }
    }, 0)
  }

  const handleRemoveOption = (field, option) => {
    if (filtersApplied) {
      setFiltersApplied(false)
    }

    let updatedFormData
    if (field === "companyName") {
      updatedFormData = { ...formData, [field]: "" }
      setFormData(updatedFormData)
    } else {
      const currentValues = [...formData[field]]
      const index = currentValues.indexOf(option)
      if (index !== -1) {
        currentValues.splice(index, 1)
        updatedFormData = { ...formData, [field]: currentValues }
        setFormData(updatedFormData)
      }
    }

    if (updatedFormData) {
      applyCascadingFilters(updatedFormData)
    }
  }

  const handleSelectAllDropdown = (field) => {
    if (field === "companyName") {
      return
    }

    if (filtersApplied) {
      setFiltersApplied(false)
    }

    const effectiveOptions =
      cascadingOptions[
        field === "projectName"
          ? "project_names"
          : field === "state"
            ? "states"
            : field === "location"
              ? "locations"
              : "project_names"
      ].length > 0
        ? cascadingOptions[
            field === "projectName"
              ? "project_names"
              : field === "state"
                ? "states"
                : field === "location"
                  ? "locations"
                  : "project_names"
          ]
        : originalDropdownOptions[
            field === "projectName"
              ? "project_names"
              : field === "state"
                ? "states"
                : field === "location"
                  ? "locations"
                  : "project_names"
          ]

    const searchTerm = searchTerms[field]
    const filteredOptions = searchTerm
      ? effectiveOptions.filter((option) => option.toLowerCase().includes(searchTerm.toLowerCase()))
      : effectiveOptions

    const updatedFormData = {
      ...formData,
      [field]: formData[field].length === filteredOptions.length ? [] : [...filteredOptions],
    }
    setFormData(updatedFormData)

    applyCascadingFilters(updatedFormData)
  }

  const handleOptionSelect = (field, option) => {
    const currentScrollTop = dropdownScrollRef.current?.scrollTop || 0

    if (filtersApplied) {
      setFiltersApplied(false)
    }

    if (field === "companyName") {
      const updatedFormData = { ...formData, [field]: option }
      setFormData(updatedFormData)
      setOpenDropdown(null)
      setSearchTerms((prev) => ({ ...prev, [field]: "" }))
      applyCascadingFilters(updatedFormData)
    } else {
      const currentValues = [...formData[field]]
      const index = currentValues.indexOf(option)

      if (index === -1) {
        currentValues.push(option)
      } else {
        currentValues.splice(index, 1)
      }

      const updatedFormData = { ...formData, [field]: currentValues }
      setFormData(updatedFormData)

      setTimeout(() => {
        if (dropdownScrollRef.current) {
          dropdownScrollRef.current.scrollTop = currentScrollTop
        }
      }, 0)

      applyCascadingFilters(updatedFormData)
    }
  }

  const applyCascadingFilters = (currentFormData) => {
    if (!Array.isArray(allFilterData) || allFilterData.length === 0) {
      console.log("No original dataset available for cascading filters")
      return
    }

    console.log("[cascade] Applying with:", currentFormData)
    console.log("[cascade] Dataset length:", allFilterData.length)

    let filteredByCompany = allFilterData
    if (isRoleOne && currentFormData.companyName) {
      filteredByCompany = allFilterData.filter((item) => item.company_name === currentFormData.companyName)
    }

    const availableProjects = [...new Set(filteredByCompany.map((item) => item.project_name).filter(Boolean))]

    let filteredByProject = filteredByCompany
    if (currentFormData.projectName && currentFormData.projectName.length > 0) {
      const selectedProjects = new Set(currentFormData.projectName)
      filteredByProject = filteredByCompany.filter((item) => selectedProjects.has(item.project_name))
    }

    const availableStates = [...new Set(filteredByProject.map((item) => item.state).filter(Boolean))]

    let filteredByState = filteredByProject
    if (currentFormData.state && currentFormData.state.length > 0) {
      const selectedStates = new Set(currentFormData.state)
      filteredByState = filteredByProject.filter((item) => selectedStates.has(item.state))
    }

    const availableLocations = [...new Set(filteredByState.map((item) => item.location).filter(Boolean))]

    setCascadingOptions((prev) => ({
      ...prev,
      project_names: availableProjects.length > 0 ? availableProjects : originalDropdownOptions.project_names,
      states: availableStates.length > 0 ? availableStates : originalDropdownOptions.states,
      locations: availableLocations.length > 0 ? availableLocations : originalDropdownOptions.locations,
    }))

    if (isRoleOne && formData.companyName !== currentFormData.companyName) {
      setFormData((prevData) => ({
        ...prevData,
        projectName: [],
        state: [],
        location: [],
      }))
    }
  }

  const toggleDropdown = (field) => {
    if (openDropdown === field) {
      setOpenDropdown(null)
    } else {
      setOpenDropdown(field)
    }
  }

  const CustomDropdown = ({ label, field, options }) => {
    const effectiveOptions =
      cascadingOptions[
        field === "companyName"
          ? "company_names"
          : field === "projectName"
            ? "project_names"
            : field === "state"
              ? "states"
              : field === "location"
                ? "locations"
                : "company_names"
      ].length > 0
        ? cascadingOptions[
            field === "companyName"
              ? "company_names"
              : field === "projectName"
                ? "project_names"
                : field === "state"
                  ? "states"
                  : field === "location"
                    ? "locations"
                    : "company_names"
          ]
        : options

    const searchTerm = searchTerms[field]
    const filteredOptions = searchTerm
      ? effectiveOptions.filter((option) => option.toLowerCase().includes(searchTerm.toLowerCase()))
      : effectiveOptions

    const isSingleSelect = field === "companyName"
    const showSearch = ["companyName", "projectName", "state", "location"].includes(field)

    const getSearchPlaceholder = (field) => {
      switch (field) {
        case "companyName":
          return "Search company names..."
        case "projectName":
          return "Search project names..."
        case "state":
          return "Search states..."
        case "location":
          return "Search locations..."
        default:
          return "Search..."
      }
    }

    return (
      <div className="relative w-full">
        <label className="block mb-2 text-sm font-medium text-white">{label}</label>
        <div
          className="flex flex-wrap gap-1 min-h-[44px] p-2 rounded-lg border cursor-pointer w-full transition-all duration-200 bg-gray-700/80 backdrop-blur-sm border-gray-600/50 hover:bg-gray-600/80 overflow-hidden"
          onClick={() => toggleDropdown(field)}
        >
          {isSingleSelect ? (
            formData[field] ? (
              <div className="flex items-center text-sm rounded-full px-2 py-1 bg-white/20 text-white max-w-full">
                <span
                  className="mr-1 truncate flex-1 min-w-0"
                  style={{ maxWidth: "calc(100% - 20px)" }}
                  title={formData[field]}
                >
                  {formData[field]}
                </span>
                <X
                  size={12}
                  className="cursor-pointer flex-shrink-0 hover:text-red-400 ml-1"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemoveOption(field, formData[field])
                  }}
                />
              </div>
            ) : (
              <span className="text-sm text-gray-400">Select {label}</span>
            )
          ) : formData[field].length > 0 ? (
            formData[field].map((option, idx) => (
              <div
                key={idx}
                className="flex items-center text-sm rounded-full px-2 py-1 bg-white/20 text-white max-w-full"
                style={{ maxWidth: "150px" }}
              >
                <span className="mr-1 truncate flex-1 min-w-0" style={{ maxWidth: "calc(100% - 20px)" }} title={option}>
                  {option}
                </span>
                <X
                  size={12}
                  className="cursor-pointer flex-shrink-0 hover:text-red-400 ml-1"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemoveOption(field, option)
                  }}
                />
              </div>
            ))
          ) : (
            <span className="text-sm text-gray-400">Select {label}</span>
          )}
          <div className="ml-auto flex items-center flex-shrink-0">
            <ChevronDown
              size={16}
              className={`transition-transform text-gray-400 ${openDropdown === field ? "rotate-180" : ""}`}
            />
          </div>
        </div>

        {openDropdown === field && (
          <>
            <div
              className="fixed inset-0 z-[9998]"
              onClick={(e) => {
                if (e.target.closest(".search-container")) {
                  return
                }
                setOpenDropdown(null)
              }}
            />
            <div
              ref={dropdownScrollRef}
              className="absolute mt-1 w-full max-h-60 overflow-auto rounded-lg backdrop-blur-md border shadow-lg z-[9999] bg-gray-800/95 border-gray-600/50"
            >
              {showSearch && (
                <div className="sticky top-0 bg-gray-900/95 p-2 border-b border-gray-600/50 search-container">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-orange-400 h-4 w-4" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchTerm}
                      onChange={(e) => handleSearchTermChange(field, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onFocus={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      placeholder={getSearchPlaceholder(field)}
                      className="w-full pl-10 pr-4 py-2 bg-gray-700/50 border border-orange-400/50 rounded-lg text-white placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                      autoComplete="off"
                    />
                  </div>
                </div>
              )}

              {!isSingleSelect && (
                <div className="sticky top-0 backdrop-blur-md border-b p-2 bg-white/20 border-gray-600/50">
                  <div
                    className="cursor-pointer rounded-lg p-2 transition-colors hover:bg-orange-600 text-white"
                    onClick={() => handleSelectAllDropdown(field)}
                  >
                    <span className="text-sm">
                      {formData[field].length === effectiveOptions.length ? "Deselect All" : "Select All"}
                    </span>
                  </div>
                </div>
              )}

              {filteredOptions.map((option, idx) => (
                <div
                  key={idx}
                  className={`cursor-pointer p-2 transition-colors ${
                    isSingleSelect
                      ? formData[field] === option
                        ? "bg-white/15"
                        : ""
                      : formData[field].includes(option)
                        ? "bg-white/15"
                        : ""
                  } hover:bg-orange-600 text-white`}
                  onClick={() => handleOptionSelect(field, option)}
                >
                  <div className="flex items-center">
                    <div
                      className={`w-4 h-4 mr-2 rounded border flex-shrink-0 ${
                        isSingleSelect
                          ? formData[field] === option
                            ? "bg-orange-500 border-orange-500"
                            : "border-white/40"
                          : formData[field].includes(option)
                            ? "bg-orange-500 border-orange-500"
                            : "border-white/40"
                      }`}
                    >
                      {((isSingleSelect && formData[field] === option) ||
                        (!isSingleSelect && formData[field].includes(option))) && (
                        <div className="flex items-center justify-center h-full">
                          <svg
                            width="10"
                            height="8"
                            viewBox="0 0 10 8"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-2.5 h-2"
                          >
                            <path
                              d="M9 1L3.5 6.5L1 4"
                              stroke="white"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                    <span className="text-sm truncate" title={option}>
                      {option}
                    </span>
                  </div>
                </div>
              ))}

              {showSearch && filteredOptions.length === 0 && (
                <div className="p-4 text-center text-white/60 text-sm">No matching {label.toLowerCase()} found</div>
              )}
            </div>
          </>
        )}
      </div>
    )
  }

  const handleApply = useCallback(async () => {
    setIsLoading(true)
    try {
      const requestData = {
        role_id: role_id,
      }

      if (!isRoleOne && client_id) {
        requestData.client_id = client_id
      }

      if (role_id === "3" || role_id === 3) {
        requestData.username = username
      }

      if (!isRoleOne && company_name) {
        requestData.company_name = company_name
      }

      if (isRoleOne && formData.companyName) {
        requestData.company_name = formData.companyName
      }

      if (formData.projectName.length > 0) {
        requestData.project_name = formData.projectName.join(",")
      }
      if (formData.state.length > 0) {
        requestData.state = formData.state.join(",")
      }
      if (formData.location.length > 0) {
        requestData.location = formData.location.join(",")
      }

      if (fromDate) {
        requestData.from_date = dayjs(fromDate).startOf("month").format("YYYY-MM-DD")
      }

      if (toDate) {
        requestData.to_date = dayjs(toDate).startOf("month").format("YYYY-MM-DD")
      }

      const res = await axios.post(`${API_BASE_URL}api/dashboard/`, requestData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      if (res.data) {
        console.log("API Response Data:", res.data)
        console.log("Dashboard Data after setting state:", {
          total_months: res.data.total_months || 0,
          yet_upload: res.data.yet_upload || 0,
          uploaded: res.data.uploaded || 0,
          total_client: res.data.total_client || 0,
          licensed_site: res.data.licensed_site || 0,
          non_licensed_site: res.data.non_licensed_site || 0,
          total_record: res.data.total_record || 0,
          total_employee: res.data.total_employee || 0,
        })
        setDashboardData({
          total_months: res.data.total_months || 0,
          yet_upload: res.data.yet_upload || 0,
          uploaded: res.data.uploaded || 0,
          total_client: res.data.total_client || 0,
          licensed_site: res.data.licensed_site || 0,
          non_licensed_site: res.data.non_licensed_site || 0,
          total_record: res.data.total_record || 0,
          total_employee: res.data.total_employee || 0,
        })
        setFiltersApplied(true)
        showSuccess("Success", "Dashboard data loaded successfully.")
      } else {
        showWarn("No Data Found", "No data returned from server.")
        setFiltersApplied(false)
      }
    } catch (err) {
      const errorMessage = handleApiError(err, "Failed to load dashboard data.")
      showError("API Error", errorMessage)
      setFiltersApplied(false)
    } finally {
      setIsLoading(false)
    }
  }, [
    role_id,
    client_id,
    username,
    isRoleOne,
    company_name,
    formData.companyName,
    formData.projectName,
    formData.state,
    formData.location,
    fromDate,
    toDate,
    token,
  ])

  const handleClear = () => {
    setFormData((prev) => ({
      ...prev,
      companyName: "",
      projectName: [],
      state: [],
      location: [],
    }))
    setFromDate(null)
    setToDate(null)
    setDashboardData({
      total_months: 0,
      yet_upload: 0,
      uploaded: 0,
      total_client: 0,
      licensed_site: 0,
      non_licensed_site: 0,
      total_record: 0,
      total_employee: 0,
    })
    setFiltersApplied(false)
    setSearchTerms((prev) => ({
      ...prev,
      companyName: "",
      projectName: "",
      state: "",
      location: "",
    }))

    setDropdownOptions(originalDropdownOptions)
    setCascadingOptions(originalDropdownOptions)
  }

  const fromDateOnChange = (newValue) => {
    if (filtersApplied) {
      setFiltersApplied(false)
    }
    setFromDate(newValue)
  }

  const toDateOnChange = (newValue) => {
    if (filtersApplied) {
      setFiltersApplied(false)
    }
    setToDate(newValue)
  }

  useEffect(() => {
    if (!token) {
      showError("Configuration Error", "Authentication token is missing. Please login again.")
      return
    }

    const requestData = {
      role_id,
      username,
    }

    if (client_id) {
      requestData.client_id = client_id
    }

    axios
      .post(`${API_BASE_URL}api/dashboard_filter/`, requestData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      .then((res) => {
        const rawData = res.data?.data || []
        console.log("Raw filter response:", rawData)

        setAllFilterData(rawData)

        let company_names = rawData.map((item) => item.company_name).filter(Boolean)
        let project_names = rawData.map((item) => item.project_name).filter(Boolean)
        let states = rawData.map((item) => item.state).filter(Boolean)
        let locations = rawData.map((item) => item.location).filter(Boolean)

        company_names = [...new Set(company_names)]
        project_names = [...new Set(project_names)]
        states = [...new Set(states)]
        locations = [...new Set(locations)]

        console.log("Dropdown data parsed:", {
          company_names,
          project_names,
          states,
          locations,
        })

        const finalDropdownOptions = {
          company_names,
          project_names,
          states,
          locations,
        }

        setDropdownOptions(finalDropdownOptions)
        setOriginalDropdownOptions(finalDropdownOptions)
        setCascadingOptions(finalDropdownOptions)
      })
      .catch((err) => {
        const errorMessage = handleApiError(err, "Failed to load filter data.")
        showError("Data Load Failed", errorMessage)
      })
  }, [token, role_id, username, client_id])

  useEffect(() => {
    if (openDropdown && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
    }
  }, [openDropdown])

  // Dashboard cards data with high-contrast gradients
  const dashboardCards = [
    {
      title: "Total Months",
      value: dashboardData.total_months,
      icon: Clock,
      description: "Total months in system",
      iconColor: "text-indigo-400",
      variant: "default",
      gradient: "from-indigo-700/30 to-blue-500/20",
      gradientId: "gradient-total-months",
    },
    {
      title: "Yet to Upload",
      value: dashboardData.yet_upload,
      icon: Upload,
      description: "Pending uploads",
      iconColor: "text-orange-400",
      variant: "default",
      gradient: "from-orange-600/30 to-amber-400/20",
      gradientId: "gradient-yet-to-upload",
    },
    {
      title: "Uploaded",
      value: dashboardData.uploaded,
      icon: TrendingUp,
      description: "Successfully uploaded",
      iconColor: "text-green-400",
      variant: "default",
      gradient: "from-green-700/30 to-lime-500/20",
      gradientId: "gradient-uploaded",
    },
    {
      title: "Total Clients",
      value: dashboardData.total_client,
      icon: Users,
      description: "Total client count",
      iconColor: "text-purple-400",
      variant: "default",
      gradient: "from-purple-700/30 to-violet-500/20",
      gradientId: "gradient-total-clients",
    },
    {
      title: "Licensed Sites",
      value: dashboardData.licensed_site,
      icon: Shield,
      description: "Licensed site count",
      iconColor: "text-teal-400",
      variant: "default",
      gradient: "from-teal-700/30 to-cyan-500/20",
      gradientId: "gradient-licensed-sites",
    },
    {
      title: "Non-Licensed Sites",
      value: dashboardData.non_licensed_site,
      icon: Building,
      description: "Non-licensed site count",
      iconColor: "text-red-400",
      variant: "default",
      gradient: "from-red-700/30 to-pink-500/20",
      gradientId: "gradient-non-licensed-sites",
    },
    {
      title: "Total Records",
      value: dashboardData.total_record,
      icon: Database,
      description: "Total record count",
      iconColor: "text-blue-400",
      variant: "default",
      gradient: "from-blue-700/30 to-sky-500/20",
      gradientId: "gradient-total-records",
    },
    {
      title: "Total Employees",
      value: dashboardData.total_employee,
      icon: FileText,
      description: "Total employee count",
      iconColor: "text-emerald-400",
      variant: "default",
      gradient: "from-emerald-700/30 to-green-500/20",
      gradientId: "gradient-total-employees",
    },
  ]

  const allCardValues = dashboardCards.map((c) => c.value)
  const maxCardValue = allCardValues.length > 0 ? Math.max(...allCardValues) : 1

  // Bar chart data (restructured for horizontal bars)
  const chartData1 = [
    { name: "Total Months", value: dashboardData.total_months, gradientId: "gradient-total-months" },
    { name: "Yet to Upload", value: dashboardData.yet_upload, gradientId: "gradient-yet-to-upload" },
    { name: "Uploaded", value: dashboardData.uploaded, gradientId: "gradient-uploaded" },
    { name: "Total Clients", value: dashboardData.total_client, gradientId: "gradient-total-clients" },
  ]

  const chartData2 = [
    { name: "Licensed Sites", value: dashboardData.licensed_site, gradientId: "gradient-licensed-sites" },
    { name: "Non-Licensed Sites", value: dashboardData.non_licensed_site, gradientId: "gradient-non-licensed-sites" },
    { name: "Total Records", value: dashboardData.total_record, gradientId: "gradient-total-records" },
    { name: "Total Employees", value: dashboardData.total_employee, gradientId: "gradient-total-employees" },
  ]

  const cardAnimations = `
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(40px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-4px) rotate(2deg);
  }
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

@keyframes barGrow {
  from {
    transform: scaleX(0);
    transform-origin: left;
  }
  to {
    transform: scaleX(1);
    transform-origin: left;
  }
}

.animate-fadeInUp {
  animation: fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.animate-float {
  animation: float 3s ease-in-out infinite;
}

.animate-shimmer {
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
}

.animate-barGrow {
  animation: barGrow 1s ease-out;
}
`

  return (
    <div className="min-h-screen flex flex-col">
      <style>{cardAnimations}</style>
      <svg width="0" height="0">
        <defs>
          <linearGradient id="gradient-total-months" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" style={{ stopColor: "rgba(99, 102, 241, 0.3)", stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: "rgba(59, 130, 246, 0.2)", stopOpacity: 1 }} />
          </linearGradient>
          <linearGradient id="gradient-yet-to-upload" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" style={{ stopColor: "rgba(234, 88, 12, 0.3)", stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: "rgba(251, 191, 36, 0.2)", stopOpacity: 1 }} />
          </linearGradient>
          <linearGradient id="gradient-uploaded" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" style={{ stopColor: "rgba(21, 128, 61, 0.3)", stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: "rgba(132, 204, 22, 0.2)", stopOpacity: 1 }} />
          </linearGradient>
          <linearGradient id="gradient-total-clients" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" style={{ stopColor: "rgba(126, 34, 206, 0.3)", stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: "rgba(139, 92, 246, 0.2)", stopOpacity: 1 }} />
          </linearGradient>
          <linearGradient id="gradient-licensed-sites" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" style={{ stopColor: "rgba(15, 118, 110, 0.3)", stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: "rgba(6, 182, 212, 0.2)", stopOpacity: 1 }} />
          </linearGradient>
          <linearGradient id="gradient-non-licensed-sites" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" style={{ stopColor: "rgba(185, 28, 28, 0.3)", stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: "rgba(236, 72, 153, 0.2)", stopOpacity: 1 }} />
          </linearGradient>
          <linearGradient id="gradient-total-records" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" style={{ stopColor: "rgba(29, 78, 216, 0.3)", stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: "rgba(14, 165, 233, 0.2)", stopOpacity: 1 }} />
          </linearGradient>
          <linearGradient id="gradient-total-employees" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" style={{ stopColor: "rgba(4, 120, 87, 0.3)", stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: "rgba(74, 222, 128, 0.2)", stopOpacity: 1 }} />
          </linearGradient>
        </defs>
      </svg>
      <Toast ref={toast} position="top-right" />
      {isLoading && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/40 backdrop-blur-md">
          <div className="p-6 rounded-2xl shadow-2xl flex flex-col items-center gap-4">
            <ClimbingBoxLoader color="#d97925" size={15} />
            <p className="text-white text-lg font-semibold">Loading...</p>
          </div>
        </div>
      )}

      <div className="flex-shrink-0 p-2 relative z-20">
        <Card className="p-3 w-full max-w-7xl mx-auto overflow-visible" variant="default" gradient="from-gray-600/20 to-gray-400/10">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-300/30">
                <Sparkles className="h-6 w-6 text-purple-300" />
              </div>
              <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-white via-purple-200 to-white">
                Analytics Dashboard
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-yellow-400 animate-pulse" />
              <span className="text-xs text-white/70">Live Data</span>
            </div>
          </div>

          <div className="space-y-4">
            <div className={`grid gap-4 ${isRoleOne ? "grid-cols-1 md:grid-cols-4" : "grid-cols-1 md:grid-cols-3"}`}>
              {isRoleOne && (
                <div className="flex-1 min-w-[200px] overflow-visible">
                  <CustomDropdown label="Company Name" field="companyName" options={dropdownOptions.company_names} />
                </div>
              )}

              <div className="flex-1 min-w-[200px] overflow-visible">
                <CustomDropdown label="Project Name" field="projectName" options={dropdownOptions.project_names} />
              </div>

              <div className="flex-1 min-w-[200px] overflow-visible">
                <CustomDropdown label="State" field="state" options={dropdownOptions.states} />
              </div>

              <div className="flex-1 min-w-[200px] overflow-visible">
                <CustomDropdown label="Location" field="location" options={dropdownOptions.locations} />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div className="flex-1 min-w-[150px]">
                <label className="block mb-2 text-sm font-medium text-white">From Date</label>
                <DatePicker
                  value={fromDate}
                  onChange={fromDateOnChange}
                  views={["year", "month"]}
                  openTo="month"
                  format="MMM YYYY"
                  slots={{ openPickerIcon: () => <CalendarDays className="text-white/70" size={16} /> }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: "outlined",
                      size: "small",
                      InputProps: {
                        style: {
                          background: "rgba(55, 65, 81, 0.8)",
                          backdropFilter: "blur(10px)",
                          borderRadius: 8,
                          height: 44,
                          color: "white",
                          border: "1px solid rgba(75, 85, 99, 0.5)",
                          fontSize: "14px",
                          textTransform: "uppercase",
                        },
                      },
                    },
                    popper: {
                      sx: {
                        zIndex: 10001,
                        "& .MuiPaper-root": {
                          background: "rgba(31, 41, 55, 0.95)",
                          backdropFilter: "blur(20px)",
                          border: "1px solid rgba(75, 85, 99, 0.5)",
                          color: "white",
                          borderRadius: "8px",
                        },
                        "& .MuiPickersDay-root": {
                          color: "white",
                          "&:hover": {
                            background: "rgba(217, 121, 37, 0.8)",
                            color: "white",
                          },
                          "&.Mui-selected": {
                            background: "rgba(217, 121, 37, 1)",
                            color: "white",
                            "&:hover": {
                              background: "rgba(217, 121, 37, 0.8)",
                            },
                          },
                        },
                        "& .MuiPickersCalendarHeader-root": {
                          color: "white",
                        },
                        "& .MuiPickersArrowSwitcher-button": {
                          color: "white",
                          "&:hover": {
                            backgroundColor: "rgba(217, 121, 37, 0.8)",
                          },
                        },
                        "& .MuiDayCalendar-weekDayLabel": {
                          color: "white",
                        },
                      },
                    },
                  }}
                />
              </div>

              <div className="flex-1 min-w-[150px]">
                <label className="block mb-2 text-sm font-medium text-white">To Date</label>
                <DatePicker
                  value={toDate}
                  onChange={toDateOnChange}
                  views={["year", "month"]}
                  openTo="month"
                  format="MMM YYYY"
                  slots={{ openPickerIcon: () => <CalendarDays className="text-white/70" size={16} /> }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: "outlined",
                      size: "small",
                      InputProps: {
                        style: {
                          background: "rgba(55, 65, 81, 0.8)",
                          backdropFilter: "blur(10px)",
                          borderRadius: 8,
                          height: 44,
                          color: "white",
                          border: "1px solid rgba(75, 85, 99, 0.5)",
                          fontSize: "14px",
                          textTransform: "uppercase",
                        },
                      },
                    },
                    popper: {
                      sx: {
                        zIndex: 10001,
                        "& .MuiPaper-root": {
                          background: "rgba(31, 41, 55, 0.95)",
                          backdropFilter: "blur(20px)",
                          border: "1px solid rgba(75, 85, 99, 0.5)",
                          color: "white",
                          borderRadius: "8px",
                        },
                        "& .MuiPickersDay-root": {
                          color: "white",
                          "&:hover": {
                            background: "rgba(217, 121, 37, 0.8)",
                            color: "white",
                          },
                          "&.Mui-selected": {
                            background: "rgba(217, 121, 37, 1)",
                            color: "white",
                            "&:hover": {
                              background: "rgba(217, 121, 37, 0.8)",
                            },
                          },
                        },
                        "& .MuiPickersCalendarHeader-root": {
                          color: "white",
                        },
                        "& .MuiPickersArrowSwitcher-button": {
                          color: "white",
                          "&:hover": {
                            backgroundColor: "rgba(217, 121, 37, 0.8)",
                          },
                        },
                        "& .MuiDayCalendar-weekDayLabel": {
                          color: "white",
                        },
                      },
                    },
                  }}
                />
              </div>

              <button
                onClick={handleApply}
                className="px-6 py-3 rounded-lg text-white font-semibold shadow-lg transition-all duration-300 text-sm h-[44px] bg-gradient-to-r from-[#d97925] to-red-500 hover:from-orange-600 hover:to-red-600 border border-orange-400/50 hover:border-orange-300"
                disabled={isLoading}
              >
                {isLoading ? "Loading..." : "Apply Filter"}
              </button>

              <button
                onClick={handleClear}
                className="px-6 py-3 rounded-lg text-white font-semibold bg-gray-700/80 hover:bg-red-600 border border-gray-600/50 hover:border-red-400 shadow-lg transition-all duration-300 text-sm h-[44px]"
                disabled={isLoading}
              >
                Clear
              </button>
            </div>
          </div>
        </Card>
      </div>

      <div className="flex-1 relative z-10">
        <div className="p-2 pt-1">
          <div className="w-full max-w-7xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4 mt-2">
              {dashboardCards.map((card, index) => {
                const IconComponent = card.icon
                return (
                  <Card
                    key={index}
                    variant={card.variant}
                    gradient={card.gradient}
                    className="h-[180px] animate-fadeInUp"
                    style={{
                      animationDelay: `${index * 150}ms`,
                      animationFillMode: "both",
                    }}
                  >
                    <CardHeader className="relative z-10 p-4 pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm">{card.title}</CardTitle>
                        <div
                          className={`p-2 rounded-xl backdrop-blur-sm bg-gradient-to-br ${card.gradient} border border-white/20 transition-all duration-300 animate-float`}
                        >
                          <IconComponent className={`h-5 w-5 ${card.iconColor}`} />
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent className="relative z-10 px-4 pb-4">
                      <div className="flex items-end justify-between mb-2">
                        <div>
                          <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-white to-white/80 mb-1">
                            <AnimatedCounter value={card.value} />
                          </div>
                          <CardDescription className="text-xs">{card.description}</CardDescription>
                        </div>
                      </div>

                      <div className="w-full backdrop-blur-sm bg-white/10 rounded-full h-2 border border-white/20 overflow-hidden">
                        <div
                          className={`h-2 rounded-full bg-gradient-to-r ${card.gradient} transition-all duration-2000 ease-out animate-shimmer`}
                          style={{
                            width: `${Math.min((card.value / maxCardValue) * 100, 100)}%`,
                          }}
                        ></div>
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
              <Card
                className="animate-fadeInUp h-[300px]"
                variant="default"
                gradient="from-gray-600/20 to-gray-400/10"
                style={{ animationDelay: `${dashboardCards.length * 150}ms`, animationFillMode: "both" }}
              >
                <CardHeader className="relative z-10 p-4 pb-2">
                  <CardTitle>Activity Metrics</CardTitle>
                </CardHeader>
                <CardContent className="relative z-10 p-4 pt-0">
                  <ResponsiveContainer width="100%" height={250}>
                    {chartData1.every((item) => item.value === 0) ? (
                      <div className="flex items-center justify-center h-full text-white/50 text-sm">
                        No Data Available
                      </div>
                    ) : (
                      <BarChart
                        layout="vertical"
                        data={chartData1}
                        margin={{ top: 20, right: 20, left: 40, bottom: 20 }}
                      >
                        <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: "white", fontSize: 10 }} />
                        <YAxis
                          dataKey="name"
                          type="category"
                          axisLine={false}
                          tickLine={false}
                          tick={{ fill: "white", fontSize: 10 }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar
                          dataKey="value"
                          barSize={30}
                          radius={[0, 10, 10, 0]}
                          className="animate-barGrow"
                        >
                          {chartData1.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={`url(#${entry.gradientId})`} />
                          ))}
                        </Bar>
                      </BarChart>
                    )}
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card
                className="animate-fadeInUp h-[300px]"
                variant="default"
                gradient="from-gray-600/20 to-gray-400/10"
                style={{ animationDelay: `${dashboardCards.length * 150 + 100}ms`, animationFillMode: "both" }}
              >
                <CardHeader className="relative z-10 p-4 pb-2">
                  <CardTitle>Site and Record Metrics</CardTitle>
                </CardHeader>
                <CardContent className="relative z-10 p-4 pt-0">
                  <ResponsiveContainer width="100%" height={250}>
                    {chartData2.every((item) => item.value === 0) ? (
                      <div className="flex items-center justify-center h-full text-white/50 text-sm">
                        No Data Available
                      </div>
                    ) : (
                      <BarChart
                        layout="vertical"
                        data={chartData2}
                        margin={{ top: 20, right: 20, left: 40, bottom: 20 }}
                      >
                        <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: "white", fontSize: 10 }} />
                        <YAxis
                          dataKey="name"
                          type="category"
                          axisLine={false}
                          tickLine={false}
                          tick={{ fill: "white", fontSize: 10 }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar
                          dataKey="value"
                          barSize={30}
                          radius={[0, 10, 10, 0]}
                          className="animate-barGrow"
                        >
                          {chartData2.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={`url(#${entry.gradientId})`} />
                          ))}
                        </Bar>
                      </BarChart>
                    )}
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard